import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useFormContext } from 'react-hook-form'
import { Checkbox } from 'spa/member/components/Checkbox'
import { CheckBoxProps } from 'spa/member/components/Checkbox/styled'
import { JobOfferSearchFormQueryBox } from './JobOfferSearchFormQueryBox'
import { Input } from 'spa/member/components/Input'
import { Button } from 'spa/member/components/Button'
import CategorizedQuerySearchConditionModal from './CategorizedQuerySearchConditionModal'
import {
  OptionGroup,
  StyledSearchConditionDropdownProps,
  StyledSearchConditionDropdownPropsWithId
} from './SearchConditionDropdown/styled'
import ListedQuerySearchConditionModal from './ListedQuerySearchConditionModal'
import { DisplayOrder } from 'spa/member/pages/JobOffers/styled'
import { API } from 'javascripts/api'

export type StyledJobOfferSearchFormProps = {
  word?: string,
  minPrice?: number,
  maxPrice?: number,
  workingDays: CheckBoxProps[],
  workStyles: CheckBoxProps[],
  jobOfferCategories: StyledSearchConditionDropdownPropsWithId[],
  prefectures: StyledSearchConditionDropdownProps[],
  skills: StyledSearchConditionDropdownProps[],
  appeals: CheckBoxProps[],
  count: number,
  selectedCategoryIds: number[],
  selectedSkillIds: number[],
  selectedPrefectureIds: number[],
  selectedAppealIds: number[],
}

const StyledContainer = styled.div`
  width: 100%;
  background-color: ${props => props.theme.members.colors.silver._10};
  padding: 1rem 1.5rem 1.5rem 1.5rem;
`

const StyledUl = styled.ul`
  padding: 0;
  margin-bottom: 0;
  font-size: .875rem;
  list-style: none;
  indent: 0;
`

const StyledLi = styled.li`
  margin-bottom: 0;
  line-height: 1.4875rem;
`

const StyledButtonContainer = styled.div`
  padding: 1.5rem 0;
`

const StyledButton = styled(Button)`
  color: ${props => props.theme.members.colors.white};
  background-color: ${props => props.theme.members.colors.silver._70};

  &:hover {
    opacity: .7;
  }
`

const StyledPriceContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  row-gap: .5rem;
  padding-top: 1rem;
  padding-bottom: 1.5rem
`

const StyledPrice = styled.div`
  display: flex;
  align-items: center;
`

const StyledInput = styled(Input)`
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  flex-grow: 1;
`

const StyledPriceInput = styled(Input)`
  flex-grow: 1;
  width: 100px;

  @media ${props => props.theme.media.md} {
    width: 160px;
  }
`

const StyledUnit = styled.p`
  margin: auto .5rem;
  white-space: nowrap;
  font-size: .875rem;
`

const StyledCheckBoxUl = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  list-style: none;
`

const StyledCheckBoxOneColumnUl = styled.ul`
  padding: 0;
  list-style: none;
  margin: 24px 0;
`

const StyledCheckBoxLi = styled.li`
  white-space: nowrap;
  margin: 0 1.625rem .5rem 0;
`

const StyledSubmitButtonContainer = styled.div`
  align-items: center;
`

const StyledCountText = styled.div`
  font-size: .75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  > span {
    font-size: 1.5rem;
    margin: auto .25rem;
  }
`

const StyledSubmitButton = styled(Button)`
  width: 100%;
  background-color: ${props => props.theme.members.colors.orange._80};
  color: ${props => props.theme.members.colors.white};

  &:hover {
    opacity: .7;
  }
`

const CategorizedQuerySearchConditionModalContainer = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
`

export const StyledJobOfferSearchForm: React.FC<StyledJobOfferSearchFormProps> = (props: StyledJobOfferSearchFormProps) => {
  const { getValues, setValue } = useFormContext()
  const [count, setCount] = useState(props.count)
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [areaModalOpen, setAreaModalOpen] = useState(false)
  const [skillModalOpen, setSkillModalOpen] = useState(false)
  const [appealModalOpen, setAppealModalOpen] = useState(false)
  const [selectedJobOfferCategoryLabels, setSelectedJobOfferCategoryLabels] = useState<{ label: string, value: number }[]>([])
  const [latestJobOfferCategoryValues, setLatestJobOfferCategoryValues] = useState<string[]>([])
  const [selectedPrefectureLabels, setSelectedPrefectureLabels] = useState<{ label: string, value: number }[]>([])
  const [latestPrefectureValues, setLatestPrefectureValues] = useState<string[]>([])
  const [selectedSkillLabels, setSelectedSkillLabels] = useState<{ label: string, value: number }[]>([])
  const [latestSkillValues, setLatestSkillValues] = useState<string[]>([])
  const [selectedAppealLabels, setSelectedAppealLabels] = useState<{ label: string, value: number }[]>([])
  const [latestAppealValues, setLatestAppealValues] = useState<string[]>([])

  const jobOfferCategoryOptions = useRef<StyledSearchConditionDropdownPropsWithId[]>([])
  const prefectureOptions = useRef<StyledSearchConditionDropdownProps[]>([])
  const skillOptions = useRef<StyledSearchConditionDropdownProps[]>([])

  const replaceJobOfferCategoryOptions = () => {
    if (!props.jobOfferCategories?.length) {
      jobOfferCategoryOptions.current = []
      return
    }

    jobOfferCategoryOptions.current = props.jobOfferCategories.map(category => ({
      ...category,
      options: category.options.map((item) => ({ ...item, onChange: handleQueryChange }))
    }))
  }

  const replacePrefectureOptions = () => {
    if (!props.prefectures?.length) {
      prefectureOptions.current = []
      return
    }

    prefectureOptions.current = props.prefectures.map(category => ({
      ...category,
      options: category.options.map((item) => ({ ...item, onChange: handleQueryChange }))
    }))
  }

  const replaceSkillOptions = () => {
    if (!props.skills?.length) {
      skillOptions.current = []
      return
    }

    skillOptions.current = props.skills.map((category) => {
      const formatOptions = category.options && category.options.map((option: CheckBoxProps) => {
        return { ...option, onChange: handleQueryChange }
      })
      const formatGroupedOptions = category.groupedOptions &&
        category.groupedOptions.map((group: OptionGroup) => {
          return {
            ...group,
            options: group.options.map((option) => {
              return { ...option, onChange: handleQueryChange }
            })
          }
        })
      return formatOptions?.length ? { ...category, options: formatOptions } : { ...category, groupedOptions: formatGroupedOptions }
    })
  }

  const fetchJobOffersCount = async (
    word?: string,
    minPrice?: number,
    maxPrice?: number,
    openonly?: boolean,
    workingDayIds?: number[],
    workStyleIds?: number[],
    categoryIds?: number[],
    prefectureIds?: number[],
    skillIds?: number[],
    appealIds?: number[]
  ) => {
    try {
      const response = await API.getUserJobOffersCount(
        word,
        minPrice,
        maxPrice,
        openonly,
        workingDayIds?.length ? workingDayIds : undefined,
        workStyleIds?.length ? workStyleIds : undefined,
        categoryIds?.length ? categoryIds : undefined,
        prefectureIds?.length ? prefectureIds : undefined,
        skillIds?.length ? skillIds : undefined,
        appealIds?.length ? appealIds : undefined
      )
      setCount(response.data.count)
    } catch (error) {
      if (API.isAxiosError(error)) {
        if (error.response && error.response.status === 404) {
          setCount(0)
        }
      }
    }
  }
  const handleQueryChange = () => {
    const word: string = getValues('word') as string
    const minPrice: number = getValues('minPrice') === '' ? undefined : parseInt(getValues('minPrice') as string)
    const maxPrice: number = getValues('maxPrice') === '' ? undefined : parseInt(getValues('maxPrice') as string)
    const openonly: boolean = getValues('openonly') as boolean
    const workingDayIdValues: string[] = getValues('workingDayIds') as string[]
    const workingDayIds: number[] = workingDayIdValues?.length ? workingDayIdValues.map((val) => parseInt(val)) : undefined
    const workStyleIdValues: string[] = getValues('workStyleIds') as string[]
    const workStyleIds: number[] = workStyleIdValues?.length ? workStyleIdValues.map((val) => parseInt(val)) : undefined
    const categoryIdValues: string[] = getValues('jobOfferCategoryIds') as string[]
    const categoryIds: number[] = categoryIdValues?.length ? categoryIdValues.map((val) => parseInt(val)) : undefined
    const prefectureIdValues: string[] = getValues('prefectureIds') as string[]
    const prefectureIds: number[] = prefectureIdValues?.length ? prefectureIdValues.map((val) => parseInt(val)) : undefined
    const skillIdValues: string[] = getValues('skillIds') as string[]
    const skillIds: number[] = skillIdValues?.length ? skillIdValues.map((val) => parseInt(val)) : undefined
    const appealIdValues: string[] = getValues('appealIds') as string[]
    const appealIds: number[] = appealIdValues?.length ? appealIdValues.map((val) => parseInt(val)) : undefined
    const order: DisplayOrder = getValues('order') as DisplayOrder
    const page: number = parseInt(getValues('page') as string)
    void fetchJobOffersCount(
      word,
      minPrice,
      maxPrice,
      openonly,
      workingDayIds,
      workStyleIds,
      categoryIds,
      prefectureIds,
      skillIds,
      appealIds
    )
  }

  const appealOptions = props.appeals?.length && props.appeals.map((item) => ({ ...item, onChange: handleQueryChange }))

  const getIdsFromOptions = (options: Array<CheckBoxProps>): string[] => {
    return options.filter(item => item.checked || item.defaultChecked).map(item => item.value.toString())
  }

  useEffect(() => {
    setCount(props.count)
  }, [props.count, props.word, props.minPrice, props.maxPrice, props.jobOfferCategories, props.prefectures, props.skills, props.appeals])

  useEffect(() => {
    if (!jobOfferCategoryOptions.current) return

    replaceJobOfferCategoryOptions()

    const selectedOptions = jobOfferCategoryOptions.current
      .map((category) => category.options)
      .flat()
      .filter((item) => props.selectedCategoryIds.includes(item.value))
      .map((item) => {
        return { label: item.label, value: item.value }
      })

    const selectedLargeCategories = jobOfferCategoryOptions.current
      .filter(category => props.selectedCategoryIds.includes(category.id))
      .map(category => {
        return { label: category.header, value: category.id }
      })

    setSelectedJobOfferCategoryLabels(selectedLargeCategories.concat(selectedOptions))
  }, [props.jobOfferCategories, props.selectedCategoryIds])

  useEffect(() => {
    if (!skillOptions) return

    replaceSkillOptions()

    const options: CheckBoxProps[] = skillOptions.current.map((category) => {
      const options: CheckBoxProps[] = category.options
      const groupedOptions: CheckBoxProps[] = category.groupedOptions && category.groupedOptions.map((group: OptionGroup) => group.options).flat()
      return options.concat(groupedOptions)
    }).flat()

    setSelectedSkillLabels(options
      .filter((item) => props.selectedSkillIds.includes(item.value))
      .map((item) => {
        return { label: item.label, value: item.value }
      })
    )
  }, [props.skills, props.selectedSkillIds])

  useEffect(() => {
    if (!prefectureOptions.current) return

    replacePrefectureOptions()

    setSelectedPrefectureLabels(prefectureOptions.current
      .map((category) => category.options)
      .flat()
      .filter((item) => props.selectedPrefectureIds.includes(item.value))
      .map((item) => {
        return { label: item.label, value: item.value }
      })
    )
  }, [props.prefectures, props.selectedPrefectureIds])

  useEffect(() => {
    if (!appealOptions) return
    setSelectedAppealLabels(appealOptions
      .filter((item) => props.selectedAppealIds.includes(item.value))
      .map((item) => {
        return { label: item.label, value: item.value }
      })
    )
  }, [props.appeals])

  useEffect(() => {
    const jobOfferCategoryIds = props.selectedCategoryIds.map(String)

    setValue('jobOfferCategoryIds', jobOfferCategoryIds)
    setLatestJobOfferCategoryValues(jobOfferCategoryIds)
  }, [props.selectedCategoryIds])

  useEffect(() => {
    const skillIds = props.selectedSkillIds.map(String)

    setValue('skillIds', skillIds)
    setLatestSkillValues(skillIds)
  }, [props.selectedSkillIds])

  useEffect(() => {
    const prefectureIds = props.selectedPrefectureIds.map(String)

    setValue('prefectureIds', prefectureIds)
    setLatestPrefectureValues(prefectureIds)
  }, [props.selectedPrefectureIds])

  useEffect(() => {
    const appealIds = props.selectedAppealIds.map(String)

    setValue('appealIds', appealIds)
    setLatestAppealValues(appealIds)
  }, [props.selectedAppealIds])

  useEffect(() => {
    setValue('minPrice', props.minPrice)
  }, [props.minPrice])

  useEffect(() => {
    setValue('maxPrice', props.maxPrice)
  }, [props.maxPrice])

  useEffect(() => {
    setValue('word', props.word)
  }, [props.word])

  useEffect(() => {
    setValue('workingDayIds', getIdsFromOptions(props.workingDays))
  }, [props.workingDays])

  useEffect(() => {
    setValue('workStyleIds', getIdsFromOptions(props.workStyles))
  }, [props.workStyles])

  return (
    <StyledContainer>
      <div>
        <JobOfferSearchFormQueryBox title='職種' dataCy='category'>
          <StyledUl>
            {selectedJobOfferCategoryLabels && selectedJobOfferCategoryLabels.map(({ label, value }) => {
              return (
                <StyledLi key={value}>{label}</StyledLi>
              )
            })}
          </StyledUl>
          <StyledButtonContainer>
            <StyledButton type='button' buttonType='SMALL' onClick={() => setCategoryModalOpen(true)}>選択する</StyledButton>
          </StyledButtonContainer>
        </JobOfferSearchFormQueryBox>
        {jobOfferCategoryOptions.current && (
          <CategorizedQuerySearchConditionModalContainer isOpen={categoryModalOpen}>
            <CategorizedQuerySearchConditionModal
              name='jobOfferCategoryIds'
              modalTitle='職種を選択する'
              categoryGroups={jobOfferCategoryOptions.current}
              count={count}
              columns={2}
              dataCy='category'
              onSubmit={() => {
                const jobOfferCategoryIds = getValues('jobOfferCategoryIds') as string[] || []
                setSelectedJobOfferCategoryLabels(jobOfferCategoryOptions.current
                  .map((category) => category.options)
                  .flat()
                  .filter((item) => jobOfferCategoryIds.includes(item.value.toString()))
                  .map((item) => {
                    return { label: item.label, value: item.value }
                  })
                )
                setCategoryModalOpen(false)
                setLatestJobOfferCategoryValues(jobOfferCategoryIds)
              }}
              onClose={() => {
                setValue('jobOfferCategoryIds', latestJobOfferCategoryValues)
                handleQueryChange()
                setCategoryModalOpen(false)
              }}
            />
          </CategorizedQuerySearchConditionModalContainer>
        )}
        <JobOfferSearchFormQueryBox title='稼働形態' dataCy='work-style'>
          <StyledCheckBoxOneColumnUl>
            {props.workStyles && props.workStyles.map((item: CheckBoxProps) => {
              return (
                <StyledCheckBoxLi key={item.value}>
                  <Checkbox name={item.name} value={item.value} label={item.label} defaultChecked={item.defaultChecked} onChange={handleQueryChange} />
                </StyledCheckBoxLi>
              )
            })}
          </StyledCheckBoxOneColumnUl>
        </JobOfferSearchFormQueryBox>
        <JobOfferSearchFormQueryBox title='エリア' dataCy='area'>
          <StyledUl>
            {selectedPrefectureLabels && selectedPrefectureLabels.map((item) => {
              return (
                <StyledLi key={item.value}>{item.label}</StyledLi>
              )
            })}
          </StyledUl>
          <StyledButtonContainer>
            <StyledButton type='button' buttonType='SMALL' onClick={() => setAreaModalOpen(true)}>選択する</StyledButton>
          </StyledButtonContainer>
        </JobOfferSearchFormQueryBox>
        {areaModalOpen && (
          <CategorizedQuerySearchConditionModal
            name='prefectureIds'
            modalTitle='エリアを選択する'
            categoryGroups={prefectureOptions.current}
            count={count}
            columns={3}
            dataCy='area'
            onSubmit={() => {
              const prefectureIds = getValues('prefectureIds') as string[] || []
              setSelectedPrefectureLabels(prefectureOptions.current
                .map((category) => category.options)
                .flat()
                .filter((item) => prefectureIds.includes(item.value.toString()))
                .map((item) => {
                  return { label: item.label, value: item.value }
                })
              )
              setAreaModalOpen(false)
              setLatestPrefectureValues(prefectureIds)
            }}
            onClose={() => {
              setValue('prefectureIds', latestPrefectureValues)
              handleQueryChange()
              setAreaModalOpen(false)
            }}
          />
        )}
        <JobOfferSearchFormQueryBox title='スキル' dataCy='skill'>
          <StyledUl>
            {selectedSkillLabels && selectedSkillLabels.map((item) => {
              return (
                <StyledLi key={item.value}>{item.label}</StyledLi>
              )
            })}
          </StyledUl>
          <StyledButtonContainer>
            <StyledButton type='button' buttonType='SMALL' onClick={() => setSkillModalOpen(true)}>選択する</StyledButton>
          </StyledButtonContainer>
        </JobOfferSearchFormQueryBox>
        {skillModalOpen && (
          <CategorizedQuerySearchConditionModal
            name='skillIds'
            modalTitle='スキルを選択する'
            columns={3}
            categoryGroups={skillOptions.current}
            count={count}
            dataCy='skill'
            onSubmit={() => {
              const skillIds = getValues('skillIds') as string[] || []
              const options: CheckBoxProps[] = skillOptions.current.map((category) => {
                const options: CheckBoxProps[] = category.options
                const groupedOptions: CheckBoxProps[] = category.groupedOptions && category.groupedOptions.map((group: OptionGroup) => group.options).flat()
                return options.concat(groupedOptions)
              }).flat()
              setSelectedSkillLabels(options
                .filter((item) => skillIds.includes(item.value.toString()))
                .map((item) => {
                  return { label: item.label, value: item.value }
                })
              )
              setSkillModalOpen(false)
              setLatestSkillValues(skillIds)
            }}
            onClose={() => {
              setValue('skillIds', latestSkillValues)
              handleQueryChange()
              setSkillModalOpen(false)
            }}
          />
        )}
        <JobOfferSearchFormQueryBox title='単価' dataCy='price'>
          <StyledPriceContainer>
            <StyledPrice>
              <StyledPriceInput name='minPrice' placeholder='600000' onBlur={handleQueryChange} />
              <StyledUnit>円／月&nbsp;〜</StyledUnit>
            </StyledPrice>
            <StyledPrice>
              <StyledPriceInput name='maxPrice' placeholder='800000' onBlur={handleQueryChange} />
              <StyledUnit>円／月</StyledUnit>
            </StyledPrice>
          </StyledPriceContainer>
        </JobOfferSearchFormQueryBox>
        <JobOfferSearchFormQueryBox title='最低稼働日数' dataCy='working-days'>
          <StyledCheckBoxUl>
            {props.workingDays && props.workingDays.map((item: CheckBoxProps) => {
              return (
                <StyledCheckBoxLi key={item.value}>
                  <Checkbox name={item.name} value={item.value} label={item.label} defaultChecked={item.defaultChecked} onChange={handleQueryChange} />
                </StyledCheckBoxLi>
              )
            })}
          </StyledCheckBoxUl>
        </JobOfferSearchFormQueryBox>
        <JobOfferSearchFormQueryBox title='こだわり' dataCy='appeal'>
          <StyledUl>
            {selectedAppealLabels && selectedAppealLabels.map((item) => {
              return (
                <StyledLi key={item.value}>{item.label}</StyledLi>
              )
            })}
          </StyledUl>
          <StyledButtonContainer>
            <StyledButton type='button' buttonType='SMALL' onClick={() => setAppealModalOpen(true)}>選択する</StyledButton>
          </StyledButtonContainer>
        </JobOfferSearchFormQueryBox >
        {appealModalOpen && (
          <ListedQuerySearchConditionModal
            name='appealIds'
            modalTitle='こだわりを選択する'
            options={appealOptions}
            count={count}
            dataCy='appeal'
            onSubmit={() => {
              const appealIds = getValues('appealIds') as string[] || []
              setSelectedAppealLabels(appealOptions
                .filter((item) => appealIds.includes(item.value.toString()))
                .map((item) => {
                  return { label: item.label, value: item.value }
                })
              )
              setAppealModalOpen(false)
              setLatestAppealValues(appealIds)
            }}
            onClose={() => {
              setValue('appealIds', latestAppealValues)
              handleQueryChange()
              setAppealModalOpen(false)
            }}
          />
        )}
        <JobOfferSearchFormQueryBox title='キーワード' dataCy='word'>
          <StyledInput name='word' placeholder='職種、言語、場所など' onBlur={handleQueryChange} />
        </JobOfferSearchFormQueryBox>
      </div>
      <StyledSubmitButtonContainer>
        <StyledCountText>該当するお仕事数<span>{count}</span>件</StyledCountText>
        <StyledSubmitButton type='submit' data-cy='job-offers-search-btn'>この条件で絞り込む</StyledSubmitButton>
      </StyledSubmitButtonContainer>
    </StyledContainer >
  )
}
